import React, {useContext, useEffect, useRef, useState} from "react";
import {Box, Stack, useMediaQuery, useTheme} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import "../flow-layout/main.css";
import {WebSettingsContext} from "../../context/WebSettingsContext";
import {SignInContext} from "../../context/SignInContext";
import {SidebarMenu} from "../flow-layout/SIdebarMenu";
import {registrySidebarMenuItems} from "../../pages/registry-project/registry-project-routes";
import {RegistryProjectHeader} from "./RegistryProjectHeader";
import {MobileNavDrawer} from "./MobileNavDrawer";
import ProjectJourney from "../../components/tour/ProjectJourney";
import FlowDesignerJourney from "../../components/tour/FlowDesignerJourney";

// Layout for a project from the Project API: project header, flow-layout sidebar. Pages that work on one language
// show their own SubProjectSelect.
export const RegistryProjectLayout = ({project, subProject, role, basePath, children}) => {
  const {settings} = useContext(WebSettingsContext);
  const {currentUser} = useContext(SignInContext);
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [menuOpen, setMenuOpen] = useState(false);

  // Guided tour: the sidebar (and Dashboard) steps on every page, the Designer's own steps on its page.
  // Starts after sign-in with ?tour=true (SignInContext) or from the help button of the sidebar.
  const isDesignerRoute = pathname === `${basePath}/flow` || pathname.startsWith(`${basePath}/flow/`);
  const [runProjectJourney, setRunProjectJourney] = useState(() => sessionStorage.getItem("project_journey") === "true");
  const [projectJourneyRunId, setProjectJourneyRunId] = useState(0);
  const [runFlowDesignerJourney, setRunFlowDesignerJourney] = useState(false);
  const [flowJourneyRunId, setFlowJourneyRunId] = useState(0);
  const previousPathRef = useRef(pathname);

  const startJourney = () => {
    if (isDesignerRoute) {
      setFlowJourneyRunId((current) => current + 1);
      setRunFlowDesignerJourney(true);
    } else {
      setProjectJourneyRunId((current) => current + 1);
      setRunProjectJourney(true);
    }
  };

  useEffect(() => {
    if (runProjectJourney) sessionStorage.removeItem("project_journey");
  }, [runProjectJourney]);

  // moving to another page ends the journey
  useEffect(() => {
    if (previousPathRef.current !== pathname) {
      previousPathRef.current = pathname;
      setRunProjectJourney(false);
      setRunFlowDesignerJourney(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (currentUser !== null) {
      const isAdmin = currentUser?.roles?.includes("admin");
      if (!isAdmin && settings?.underMaintenance) {
        navigate("/under-maintenance");
      }
    }
  }, [settings, currentUser, navigate]);

  return (
    <Stack direction={"column"} sx={{width: "100vw", height: "100vh", overflow: "hidden", bgcolor: "transparent"}}>
      <RegistryProjectHeader project={project} role={role} onOpenMenu={() => setMenuOpen(true)}/>
      <Box sx={{flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "row"}}>
        {isMobile ? (
          <MobileNavDrawer
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            project={project}
            subProject={subProject}
            role={role}
            basePath={basePath}
            onStartJourney={startJourney}
          />
        ) : (
          <SidebarMenu
            items={registrySidebarMenuItems}
            project={{basePath, project, subProject}}
            userRole={role}
            variant={"pill"}
            onStartJourney={startJourney}
          />
        )}
        {/* Pages sit on the light grey dashboard surface; header and sidebar stay white */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          overflow: "auto",
          bgcolor: "#F7F7FB",
          display: "flex",
          flexDirection: "column"
        }}>
          <ProjectJourney
            registry
            run={runProjectJourney && !isDesignerRoute}
            pathname={pathname}
            dashboardPath={basePath}
            userRole={role}
            runId={projectJourneyRunId}
            onFinish={() => setRunProjectJourney(false)}
          />
          <FlowDesignerJourney
            registry
            run={(runFlowDesignerJourney || runProjectJourney) && isDesignerRoute}
            runId={flowJourneyRunId + (runProjectJourney ? 1 : 0)}
            onFinish={() => {
              setRunFlowDesignerJourney(false);
              setRunProjectJourney(false);
            }}
          />
          {children}
        </Box>
      </Box>
    </Stack>
  );
};
